import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sjmf_session_token";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: () => {},
  logout: async () => {},
  refresh: async () => {},
  setSessionToken: () => {},
});

const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
const setStoredToken = (t) => {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
};

const buildAuthHeaders = () => {
  const t = getStoredToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: buildAuthHeaders(),
      });
      setUser(data);
    } catch {
      // Don't clear the stored token on a transient 401 — the user might just
      // need to retry. The token is only cleared on explicit logout.
      // If the user actually signed out elsewhere, they'll see the login screen
      // and can sign in fresh.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Initial fetch
    refresh();
    // One automatic retry after a brief delay — covers cold-start race conditions
    // where the very first /api/auth/me hits before the session is fully attached.
    const retry = setTimeout(() => {
      if (!getStoredToken()) return;            // nothing to retry with
      // Only retry if we ended up logged out
      // (state is closed-over via ref via effect cleanup; just always retry —
      // refresh() is idempotent and only updates state)
      refresh();
    }, 1200);
    return () => clearTimeout(retry);
  }, [refresh]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
        headers: buildAuthHeaders(),
      });
    } catch { /* noop */ }
    setStoredToken(null);
    setUser(null);
  };

  // Used by AuthCallback after exchanging session_id
  const setSessionToken = (token) => setStoredToken(token);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setSessionToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Helper exposed so other modules (e.g., Dashboard fetches) can attach the token
export const authHeaders = buildAuthHeaders;
